// Apex Cure cart rewards engine: free-gift auto-add/upgrade/remove, confetti,
// upsell adds, analytics. Loaded as a module from the cart drawer; singleton.
import { sectionRenderer } from '@theme/section-renderer';

if (!window.__acRewardsInit) {
  window.__acRewardsInit = true;

  /* ---------- analytics ---------- */
  window.crTrack = window.crTrack || function (name, params) {
    var payload = Object.assign({ event: 'cr_' + name }, params || {});
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(payload);
    } catch (e) { /* noop */ }
    try {
      if (typeof window.gtag === 'function') window.gtag('event', 'cr_' + name, params || {});
    } catch (e) { /* noop */ }
  };

  var busy = false;
  var queued = false;

  function readConfig() {
    var el = document.querySelector('[data-ac-rewards-config]');
    if (!el) return null;
    try { return JSON.parse(el.textContent); } catch (e) { return null; }
  }

  function nonGiftSubtotal(cart) {
    return cart.items.reduce(function (sum, item) {
      if (item.properties && item.properties._gift) return sum;
      return sum + item.final_line_price;
    }, 0);
  }

  function currentTier(cfg, subtotal) {
    var tier = null;
    cfg.tiers.forEach(function (t) {
      if (subtotal >= t.threshold && (!tier || t.threshold > tier.threshold)) tier = t;
    });
    return tier;
  }

  async function cartFetch(path, body) {
    var res = await fetch(path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (!res.ok) throw new Error(path + ' HTTP ' + res.status);
    return res.json();
  }

  async function refreshDrawer() {
    try {
      await sectionRenderer.renderSection('cart-drawer-section', { cache: false });
    } catch (e) { /* drawer refresh is cosmetic; state is server-side */ }
  }

  async function reconcile(trigger) {
    if (busy) { queued = true; return; }
    busy = true;
    try {
      var cfg = readConfig();
      if (!cfg || !cfg.tiers || !cfg.tiers.length) return;

      var cart = await (await fetch('/cart.js')).json();
      var subtotal = nonGiftSubtotal(cart);
      var desired = currentTier(cfg, subtotal);
      var desiredVid = desired && desired.variantId ? String(desired.variantId) : null;

      var giftLines = cart.items.filter(function (i) { return i.properties && i.properties._gift; });
      var mutated = false;

      // Remove wrong/extra gift lines
      for (var i = 0; i < giftLines.length; i++) {
        var line = giftLines[i];
        var keep = desiredVid && String(line.variant_id) === desiredVid && !mutated;
        if (!keep || line.quantity !== 1) {
          if (keep && line.quantity !== 1) {
            await cartFetch('/cart/change.js', { id: line.key, quantity: 1 });
          } else if (!keep) {
            await cartFetch('/cart/change.js', { id: line.key, quantity: 0 });
          }
          mutated = true;
        }
      }

      // Add missing gift
      var hasDesired = giftLines.some(function (l) { return desiredVid && String(l.variant_id) === desiredVid; });
      if (desiredVid && !hasDesired) {
        await cartFetch('/cart/add.js', {
          id: Number(desiredVid),
          quantity: 1,
          properties: { _gift: 'true', _gift_tier: String(desired.tier) },
        });
        mutated = true;
        window.crTrack('reward_gift_added', { tier: desired.tier, label: desired.label });
      }

      // Tier-unlock celebration (fires on threshold crossing even without a gift product)
      var lastTier = parseInt(sessionStorage.getItem('ac_reward_tier') || '0', 10);
      var nowTier = desired ? desired.tier : (function () {
        var t = 0;
        cfg.tiers.forEach(function (x) { if (subtotal >= x.threshold) t = Math.max(t, x.tier); });
        return t;
      })();
      if (nowTier > lastTier) {
        confetti();
        window.crTrack('reward_tier_unlocked', { tier: nowTier, subtotal: subtotal });
      }
      if (nowTier !== lastTier) sessionStorage.setItem('ac_reward_tier', String(nowTier));

      if (mutated) await refreshDrawer();
      updateUpsellAway(cfg, subtotal);
      document.dispatchEvent(new CustomEvent('ac:cart-changed', { detail: { subtotal: subtotal } }));
    } catch (e) {
      /* Never break the cart over rewards; log for diagnostics. */
      console.warn('[ac-rewards]', e);
    } finally {
      busy = false;
      if (queued) { queued = false; reconcile('queued'); }
    }
  }

  function updateUpsellAway(cfg, subtotal) {
    var el = document.querySelector('[data-ac-upsell-away]');
    if (!el) return;
    var next = null;
    cfg.tiers.forEach(function (t) {
      if (subtotal < t.threshold && (!next || t.threshold < next.threshold)) next = t;
    });
    el.textContent = next
      ? 'Add ₹' + new Intl.NumberFormat('en-IN').format(Math.ceil((next.threshold - subtotal) / 100)) + ' more to unlock ' + next.label.toLowerCase()
      : '';
    // hide upsell cards for products already in the cart
    fetch('/cart.js').then(function (r) { return r.json(); }).then(function (cart) {
      var inCart = {};
      cart.items.forEach(function (i) { inCart[String(i.product_id)] = true; });
      var anyVisible = false;
      document.querySelectorAll('[data-ac-upsell-card]').forEach(function (card) {
        card.hidden = !!inCart[card.dataset.productId];
        if (!card.hidden) anyVisible = true;
      });
      var wrap = document.querySelector('[data-ac-upsell]');
      if (wrap) wrap.hidden = !anyVisible;
    }).catch(function () {});
  }

  /* ---------- upsell add buttons (event delegation survives drawer morphs) ---------- */
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-ac-upsell-add]');
    if (!btn) return;
    e.preventDefault();
    btn.disabled = true;
    cartFetch('/cart/add.js', { id: Number(btn.dataset.variantId), quantity: 1 })
      .then(function () {
        window.crTrack('reward_upsell_added', { variant_id: btn.dataset.variantId, price: Number(btn.dataset.price) });
        return refreshDrawer();
      })
      .then(function () { return reconcile('upsell'); })
      .catch(function (err) { console.warn('[ac-rewards] upsell add failed', err); })
      .finally(function () { btn.disabled = false; });
  });

  /* ---------- confetti (lightweight, no deps) ---------- */
  function confetti() {
    try {
      var c = document.createElement('canvas');
      c.style.cssText = 'position:fixed;inset:0;pointer-events:none;z-index:9999;width:100vw;height:100vh;';
      c.width = innerWidth; c.height = innerHeight;
      document.body.appendChild(c);
      var ctx = c.getContext('2d');
      var colors = ['#B08D3E', '#C9A755', '#0A2563', '#00B5F5', '#FBF7EF'];
      var parts = [];
      for (var i = 0; i < 90; i++) {
        parts.push({
          x: c.width / 2 + (Math.random() - 0.5) * 160,
          y: c.height * 0.35,
          vx: (Math.random() - 0.5) * 9,
          vy: -Math.random() * 9 - 3,
          s: Math.random() * 5 + 3,
          r: Math.random() * Math.PI,
          vr: (Math.random() - 0.5) * 0.25,
          col: colors[i % colors.length],
        });
      }
      var frames = 0;
      (function tick() {
        ctx.clearRect(0, 0, c.width, c.height);
        parts.forEach(function (p) {
          p.x += p.vx; p.y += p.vy; p.vy += 0.28; p.r += p.vr;
          ctx.save(); ctx.translate(p.x, p.y); ctx.rotate(p.r);
          ctx.fillStyle = p.col; ctx.fillRect(-p.s / 2, -p.s / 2, p.s, p.s * 0.6);
          ctx.restore();
        });
        if (++frames < 110) requestAnimationFrame(tick);
        else c.remove();
      })();
    } catch (e) { /* celebration is optional */ }
  }

  /* ---------- triggers ---------- */
  function onCartEvent(label) {
    reconcile(label);
    // The drawer section morph lands slightly after the event; run a second
    // pass so we read the freshly-rendered tier config.
    setTimeout(function () { reconcile(label + ':post-morph'); }, 700);
  }
  document.addEventListener('shopify:cart:lines-update', function () { onCartEvent('lines-update'); });
  document.addEventListener('shopify:cart:discount-update', function () { onCartEvent('discount-update'); });

  // Catch-all: any cart mutation through fetch (PDP add, sticky bar, apps)
  // triggers reconciliation, independent of theme event coverage.
  var origFetch = window.fetch;
  window.fetch = function () {
    var url = typeof arguments[0] === 'string' ? arguments[0] : (arguments[0] && arguments[0].url) || '';
    var p = origFetch.apply(this, arguments);
    if (/\/cart\/(add|change|update|clear)/.test(url)) {
      p.then(function () { onCartEvent('fetch:' + url.split('?')[0]); }).catch(function () {});
    }
    return p;
  };

  // Initial pass (covers page load with pre-filled cart)
  reconcile('init');
}
